import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";

import {
  DomainBaseComponent,
  wrapBusy,
} from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import {
  BehaviorCheck,
  OrderAnalysis,
  OrderAnalysisCard,
} from "@/model/entities/-order-analysis";

@Component({
  selector: "order-analysis",
  templateUrl: "./order-analysis.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderAnalysisComponent
  extends DomainBaseComponent
  implements OnInit {
  // --- List state ---
  cards: OrderAnalysisCard[] = [];
  totalCount = 0;
  skip = 0;
  take = 50;
  accountNameContains = "";
  isLoadingList = false;
  private accountFilter$ = new Subject<string>();

  // --- Drawer (single-JO analysis) state ---
  drawerVisible = false;
  analyzingId: number | null = null;
  isAnalyzing = false;
  result: OrderAnalysis | null = null;
  analysisError: string | null = null;

  // --- Lookup-shortcut state ---
  lookupKey = "";

  constructor(
    protected domainService: DomainService,
    private route: ActivatedRoute,
  ) {
    super(domainService);

    this.accountFilter$
      .pipe(debounceTime(300), takeUntil(this.onDestroy))
      .subscribe(() => this.reloadList());

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe((qp) => {
      const key = qp.get("joKey");
      if (key && key !== this.lookupKey) {
        this.lookupKey = key;
        this.openDrawerFor(key);
      }
    });

    this.isPageReady = true;
  }

  async ngOnInit() {
    super.ngOnInit();
    await this.reloadList();
  }

  // --- List ---

  @wrapBusy
  async reloadList() {
    this.isLoadingList = true;
    try {
      const r = await this.dbQueryService.getOrderAnalysisCards(
        this.skip,
        this.take,
        this.accountNameContains,
      );
      this.cards = r.cards;
      this.totalCount = r.totalCount;
    } finally {
      this.isLoadingList = false;
    }
  }

  async onAccountFilterChange() {
    this.skip = 0;
    this.accountFilter$.next(this.accountNameContains);
  }

  async loadMore() {
    if (this.cards.length >= this.totalCount) return;
    this.skip = this.cards.length;
    this.isLoadingList = true;
    try {
      const r = await this.dbQueryService.getOrderAnalysisCards(
        this.skip,
        this.take,
        this.accountNameContains,
      );
      this.cards = [...this.cards, ...r.cards];
      this.totalCount = r.totalCount;
    } finally {
      this.isLoadingList = false;
    }
  }

  // --- Drawer / analyze ---

  async analyzeCard(card: OrderAnalysisCard) {
    this.analyzingId = card.joHeaderId;
    await this.openDrawerFor(card.joHeaderId.toString());
  }

  async openDrawerFor(key: string) {
    const trimmed = (key || "").trim();
    if (!trimmed) return;
    this.drawerVisible = true;
    this.isAnalyzing = true;
    this.analysisError = null;
    this.result = null;
    try {
      this.result = await this.dbQueryService.getOrderAnalysis(trimmed);
      const idNum = this.result?.joHeader?.id ?? null;
      if (idNum != null) this.analyzingId = idNum;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { joKey: trimmed },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
    } catch (e: any) {
      this.analysisError = e?.error?.message ?? e?.message ?? "Lookup failed.";
    } finally {
      this.isAnalyzing = false;
    }
  }

  onDrawerHide() {
    this.analyzingId = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { joKey: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  async onLookupSubmit() {
    if (!this.lookupKey?.trim()) return;
    await this.openDrawerFor(this.lookupKey);
  }

  onLookupKeyup(event: KeyboardEvent) {
    if (event.key === "Enter") this.onLookupSubmit();
  }

  // --- Display helpers ---

  redFlagSeverityTag(sev: string): "danger" | "warn" | "info" | "secondary" {
    switch (sev) {
      case "Critical": return "danger";
      case "Warning": return "warn";
      case "Info": return "info";
      default: return "secondary";
    }
  }

  checkStatusTag(status: BehaviorCheck["status"]): "success" | "danger" | "warn" | "secondary" {
    switch (status) {
      case "OK": return "success";
      case "Fail": return "danger";
      case "Pending": return "warn";
      case "NA": return "secondary";
    }
  }

  checkStatusIcon(status: BehaviorCheck["status"]): string {
    switch (status) {
      case "OK": return "pi pi-check-circle";
      case "Fail": return "pi pi-times-circle";
      case "Pending": return "pi pi-clock";
      case "NA": return "pi pi-minus-circle";
    }
  }

  shortGuid(g: string | null): string {
    return g ? g.slice(0, 8) : "—";
  }

  groupedChecks(): { category: string; checks: BehaviorCheck[] }[] {
    const order = ["Payment", "Shipping", "Inventory", "Purchasing", "Add-ons"];
    const checks = this.result?.behaviorChecks ?? [];
    return order
      .map((category) => ({
        category,
        checks: checks.filter((c) => c.category === category),
      }))
      .filter((g) => g.checks.length > 0);
  }
}
